/**
 * Compact AI Suggestions Drawer
 * Minimal, non-intrusive UI for AI field suggestions
 *
 * Features:
 * - Collapsible drawer (slides in/out)
 * - Compact table view instead of cards
 * - Quick action buttons
 * - Minimal visual footprint
 * - Shows only essential info
 */

import React, { useState } from 'react';
import {
  Box, Drawer, Typography, IconButton, Badge, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Button, Collapse, Paper, Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { useTranslation } from 'react-i18next';
import { useDirectionContext } from '../contexts/DirectionContext';

// Confidence color coding
const getConfidenceColor = (confidence) => {
  if (confidence >= 0.9) return '#4caf50';
  if (confidence >= 0.7) return '#ff9800';
  return '#f44336';
};

const getConfidenceLabel = (confidence) => {
  if (confidence >= 0.9) return 'גבוה';
  if (confidence >= 0.7) return 'בינוני';
  return 'נמוך';
};

/**
 * Compact Suggestion Row Component
 */
function SuggestionRow({ suggestion, index, onAccept, onReject, isAccepted, isRejected }) {
  const { t } = useTranslation();

  const handleDragStart = (e) => {
    // Store the suggestion data for drop handling
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'ai-suggestion',
      suggestion: suggestion,
      index: index
    }));
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <TableRow
      draggable={!isAccepted && !isRejected}
      onDragStart={handleDragStart}
      sx={{
        backgroundColor: isAccepted ? '#f1f8f4' : isRejected ? '#fef1f1' : 'white',
        opacity: isRejected ? 0.5 : 1,
        cursor: (!isAccepted && !isRejected) ? 'grab' : 'default',
        '&:hover': {
          backgroundColor: isAccepted ? '#e8f5e9' : isRejected ? '#ffebee' : '#f5f5f5'
        },
        '&:active': {
          cursor: (!isAccepted && !isRejected) ? 'grabbing' : 'default'
        }
      }}
    >
      {/* Field Type */}
      <TableCell sx={{ py: 1, px: 2, maxWidth: 200 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
          {suggestion.fieldTypeLabel || suggestion.fieldType}
        </Typography>
      </TableCell>

      {/* Detected Label (truncated) */}
      <TableCell sx={{ py: 1, px: 2, maxWidth: 150 }}>
        <Typography
          variant="caption"
          sx={{
            color: '#666',
            fontSize: '0.75rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block'
          }}
        >
          {suggestion.label}
        </Typography>
      </TableCell>

      {/* Confidence Badge */}
      <TableCell sx={{ py: 1, px: 2 }}>
        <Chip
          label={`${(suggestion.confidence * 100).toFixed(0)}%`}
          size="small"
          sx={{
            height: 20,
            fontSize: '0.7rem',
            backgroundColor: getConfidenceColor(suggestion.confidence),
            color: 'white',
            fontWeight: 'bold'
          }}
        />
      </TableCell>

      {/* Quick Actions */}
      <TableCell sx={{ py: 1, px: 1, textAlign: 'left' }}>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={t('createTemplate.aiPanel.accept')}>
            <IconButton
              size="small"
              onClick={() => onAccept(suggestion, index)}
              disabled={isRejected || isAccepted}
              sx={{
                color: isAccepted ? '#4caf50' : '#666',
                '&:hover': { color: '#4caf50', backgroundColor: '#f1f8f4' },
                padding: '4px'
              }}
            >
              <CheckCircleIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title={t('createTemplate.aiPanel.reject')}>
            <IconButton
              size="small"
              onClick={() => onReject(suggestion, index)}
              disabled={isAccepted || isRejected}
              sx={{
                color: isRejected ? '#f44336' : '#666',
                '&:hover': { color: '#f44336', backgroundColor: '#fef1f1' },
                padding: '4px'
              }}
            >
              <CancelIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
}

/**
 * Main Compact Drawer Component
 */
export default function AISuggestedFieldsCompact({
  suggestions = [],
  onAccept,
  onReject,
  onAcceptAll,
  onRejectAll,
  acceptedFields = [],
  rejectedFields = [],
  open = true,
  onClose
}) {
  const { t } = useTranslation();
  const direction = useDirectionContext();
  const [localAccepted, setLocalAccepted] = useState(acceptedFields);
  const [localRejected, setLocalRejected] = useState(rejectedFields);
  const [expanded, setExpanded] = useState(true);

  // Determine drawer position based on text direction
  // RTL (Hebrew/Arabic): drawer on LEFT side (so content flows right to left from the main area)
  // LTR (English): drawer on RIGHT side (so content flows left to right from the main area)
  const drawerAnchor = direction === 'rtl' ? 'left' : 'right';

  const handleAccept = (suggestion, index) => {
    setLocalAccepted(prev => [...prev, index]);
    if (onAccept) onAccept(suggestion, index);
  };

  const handleReject = (suggestion, index) => {
    setLocalRejected(prev => [...prev, index]);
    if (onReject) onReject(suggestion, index);
  };

  const handleAcceptAll = () => {
    const allIndexes = suggestions.map((_, idx) => idx);
    setLocalAccepted(allIndexes);
    if (onAcceptAll) onAcceptAll();
  };

  const handleRejectAll = () => {
    const allIndexes = suggestions.map((_, idx) => idx);
    setLocalRejected(allIndexes);
    if (onRejectAll) onRejectAll();
  };

  const pendingCount = suggestions.length - localAccepted.length - localRejected.length;
  const acceptedCount = localAccepted.length;

  return (
    <Drawer
      anchor={drawerAnchor}
      open={open}
      onClose={onClose}
      variant="permanent"
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        position: 'fixed',
        zIndex: 1200,
        '& .MuiDrawer-paper': {
          width: expanded ? 500 : 60,
          boxSizing: 'border-box',
          marginTop: '64px',
          height: 'calc(100vh - 64px)',
          // Dynamic border and shadow based on direction
          // RTL: panel on left, so border/shadow on right side
          // LTR: panel on right, so border/shadow on left side
          ...(direction === 'rtl' ? {
            borderRight: '1px solid #e0e0e0',
            boxShadow: '4px 0 8px rgba(0,0,0,0.2)',
            left: 0,
          } : {
            borderLeft: '1px solid #e0e0e0',
            boxShadow: '-4px 0 8px rgba(0,0,0,0.2)',
            right: 0,
          }),
          transition: 'width 0.3s ease',
          overflowX: 'hidden',
          position: 'fixed',
          top: 64
        }
      }}
    >
      {/* Drawer Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: expanded ? 2 : 1,
          borderBottom: '2px solid #2196f3',
          backgroundColor: '#f5f9ff',
          minHeight: 64
        }}
      >
        {expanded ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SmartToyIcon sx={{ color: '#2196f3' }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                {t('createTemplate.aiPanel.title')}
              </Typography>
              <Badge badgeContent={pendingCount} color="primary" />
            </Box>

            <IconButton size="small" onClick={() => setExpanded(false)}>
              <ExpandLessIcon />
            </IconButton>
          </>
        ) : (
          <Tooltip
            title={t('createTemplate.aiPanel.showTitle')}
            placement={direction === 'rtl' ? 'right' : 'left'}
          >
            <IconButton onClick={() => setExpanded(true)} sx={{ margin: 'auto' }}>
              <Badge badgeContent={pendingCount} color="primary">
                <SmartToyIcon sx={{ color: '#2196f3' }} />
              </Badge>
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Drawer Content (only shown when expanded) */}
      {expanded && (
        <>
          {/* Stats Row */}
          <Box sx={{ padding: 2, backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', gap: 1, marginBottom: 1.5 }}>
              <Chip
                label={`${t('createTemplate.aiPanel.total')}: ${suggestions.length}`}
                size="small"
                sx={{ backgroundColor: '#2196f3', color: 'white', fontSize: '0.75rem' }}
              />
              <Chip
                label={`${t('createTemplate.aiPanel.accepted')}: ${acceptedCount}`}
                size="small"
                sx={{ backgroundColor: '#4caf50', color: 'white', fontSize: '0.75rem' }}
              />
              <Chip
                label={`${t('createTemplate.aiPanel.pending')}: ${pendingCount}`}
                size="small"
                sx={{ backgroundColor: '#ff9800', color: 'white', fontSize: '0.75rem' }}
              />
            </Box>

            {/* Bulk Actions */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={handleAcceptAll}
                startIcon={<DoneAllIcon />}
                sx={{ fontSize: '0.75rem', py: 0.5 }}
              >
                {t('createTemplate.aiPanel.acceptAll')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={handleRejectAll}
                startIcon={<DeleteSweepIcon />}
                sx={{ fontSize: '0.75rem', py: 0.5 }}
              >
                {t('createTemplate.aiPanel.rejectAll')}
              </Button>
            </Box>
          </Box>

          {/* Suggestions Table */}
          <TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, backgroundColor: '#fafafa' }}>
                    {t('createTemplate.aiPanel.fieldType')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, backgroundColor: '#fafafa' }}>
                    {t('createTemplate.aiPanel.label')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, backgroundColor: '#fafafa' }}>
                    {t('createTemplate.aiPanel.confidence')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1, backgroundColor: '#fafafa' }}>
                    {t('createTemplate.aiPanel.actions')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {suggestions.map((suggestion, index) => (
                  <SuggestionRow
                    key={index}
                    suggestion={suggestion}
                    index={index}
                    onAccept={handleAccept}
                    onReject={handleReject}
                    isAccepted={localAccepted.includes(index)}
                    isRejected={localRejected.includes(index)}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Footer Help Text */}
          <Box sx={{ padding: 1.5, borderTop: '1px solid #e0e0e0', backgroundColor: '#fafafa' }}>
            <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem' }}>
              {t('createTemplate.aiPanel.helpText')}
            </Typography>
          </Box>
        </>
      )}
    </Drawer>
  );
}
